<template>
  <v-row>
    <!-- Калькулятор цен (22.10.2026, просьба начальника): клиент прикидывает
         стоимость до подачи заявки. Считает тот же серверный расчёт, что
         показывает «Стоимость по прайс-листу» в форме заявки, — цифры
         совпадают: персональный прайс клиента поверх общего, школьный прайс
         по галочке, ступени цены за группу, обслуживание и услуги. -->
    <v-col
      cols="12"
      md="6"
    >
      <v-card>
        <v-card-title>{{ $t('Расчёт стоимости') }}</v-card-title>
        <v-card-subtitle>
          {{ $t('Выберите музей, объект и количество билетов — стоимость посчитается по вашему прайс-листу') }}
        </v-card-subtitle>
        <v-card-text>
          <!-- Радиогид и аудиогид в расчёте (шеф, 30.10.2026): отдельный заказ
               оборудования — аппараты × цена за аппарат + доставка по зоне, как
               в разделе «Радиогид / Аудиогид». -->
          <v-select
            v-model="form.kind"
            :items="kindOptions"
            item-text="name"
            item-value="value"
            :label="$t('Что считаем')"
            hide-details="auto"
            outlined
            dense
            class="mb-4"
            @change="onKindChange"
          ></v-select>
          <template v-if="isEquipment">
            <v-select
              v-if="form.kind !== 'rg-city'"
              v-model="form.object"
              :items="equipmentObjects"
              :label="$t('Выберите музей')"
              :item-text="'name' + localeSuffix"
              item-value="system_id"
              :no-data-text="$t('Ничего не найдено')"
              :loading="loadingObjects"
              hide-details="auto"
              outlined
              dense
              class="mb-4"
              @change="onEquipmentObjectChange"
            ></v-select>
            <template v-if="selectedObject">
              <datepicker
                :key="datepickerKey"
                :date-value="form.date"
                on-page="price-calculator"
                @newDate="form.date = $event.date"
              ></datepicker>
              <p class="caption text--secondary mt-1 mb-4">
                {{ $t('Дата нужна, чтобы взять прайс-лист, действующий на день посещения') }}
              </p>
              <v-text-field
                v-model="form.devices"
                v-non-negative
                type="number"
                outlined
                dense
                class="mb-2"
                :label="$t('Кол-во аппаратов')"
                placeholder="0"
                hide-details="auto"
              ></v-text-field>
              <template v-if="form.kind !== 'ag'">
                <v-checkbox
                  v-model="form.delivery"
                  hide-details
                  dense
                  class="mt-2"
                  :label="$t('Оформить доставку (услуга платная)')"
                  @change="val => { if (!val) form.deliveryZone = '' }"
                ></v-checkbox>
                <div
                  v-if="form.delivery"
                  class="d-flex flex-wrap"
                  style="gap: 24px"
                >
                  <v-checkbox
                    :input-value="form.deliveryZone === 'in'"
                    :disabled="form.deliveryZone === 'out'"
                    hide-details
                    dense
                    class="mt-2 pt-0"
                    :label="$t('Доставка в пределах КАД')"
                    @change="val => { form.deliveryZone = val ? 'in' : '' }"
                  ></v-checkbox>
                  <v-checkbox
                    :input-value="form.deliveryZone === 'out'"
                    :disabled="form.deliveryZone === 'in'"
                    hide-details
                    dense
                    class="mt-2 pt-0"
                    :label="$t('Доставка за пределы КАД')"
                    @change="val => { form.deliveryZone = val ? 'out' : '' }"
                  ></v-checkbox>
                </div>
              </template>
            </template>
          </template>
          <v-select
            v-if="!isEquipment"
            v-model="form.type"
            :items="typeOptions"
            item-text="name"
            item-value="value"
            :label="$t('Выберите категорию группы')"
            hide-details="auto"
            outlined
            dense
            class="mb-4"
            @change="onTypeChange"
          ></v-select>
          <!-- Как в форме заявки: категория группы остаётся, цены — из
               школьного прайс-листа. -->
          <v-checkbox
            v-if="!isEquipment"
            v-model="form.school"
            hide-details
            dense
            class="mt-0 mb-4"
            :label="$t('Школьная группа')"
          ></v-checkbox>
          <v-select
            v-if="!isEquipment"
            v-model="form.museum"
            :items="filteredMuseums"
            :label="$t('Выберите музей')"
            :item-text="'name' + localeSuffix"
            item-value="id"
            :no-data-text="$t('Ничего не найдено')"
            :loading="loadingMuseums"
            hide-details="auto"
            outlined
            dense
            class="mb-4"
            @change="onMuseumChange"
          >
            <template v-slot:prepend-item>
              <v-list-item>
                <v-list-item-content class="py-1">
                  <v-text-field
                    v-model="museumSearch"
                    :placeholder="$t('Поиск')"
                    outlined
                    dense
                    hide-details
                    clearable
                    @click.stop
                  ></v-text-field>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mb-1"></v-divider>
            </template>
          </v-select>

          <v-select
            v-if="!isEquipment && form.museum"
            v-model="form.object"
            :items="objects"
            :label="$t('Выберите объект')"
            :item-text="'name' + localeSuffix"
            item-value="system_id"
            :no-data-text="$t('Ничего не найдено')"
            :loading="loadingObjects"
            hide-details="auto"
            outlined
            dense
            class="mb-4"
            @change="onObjectChange"
          ></v-select>

          <template v-if="!isEquipment && selectedObject">
            <p
              v-if="selectedObject.info_message"
              class="caption mb-4"
            >
              {{ selectedObject.info_message }}
            </p>
            <!-- Порог радиогида (шеф, 02.11.2026): музей требует радиогид от N
                 человек — поле «Количество человек для обязательного заказа РГ»
                 у объекта в админке. Подпись появляется, когда группа дошла до
                 порога (04.11.2026: раньше была видна сразу, но в админке у
                 17 объектов стоит заглушка «100», и «от 100 человек» висело
                 даже на услугах гида). Это только предупреждение: в сумму
                 радиогид сам не добавляется. -->
            <p
              v-if="rgMandatory"
              class="caption mb-4 error--text font-weight-semibold"
            >
              {{ $t('При группе от {n} человек требуется радиогид', { n: rgThreshold }) }}
            </p>

            <!-- Дата нужна расчёту: прайс-лист берётся действующий на день
                 посещения (с нового года цены могут быть другими). -->
            <datepicker
              :key="datepickerKey"
              :date-value="form.date"
              on-page="price-calculator"
              @newDate="form.date = $event.date"
            ></datepicker>
            <p class="caption text--secondary mt-1 mb-4">
              {{ $t('Дата нужна, чтобы взять прайс-лист, действующий на день посещения') }}
            </p>

            <v-text-field
              v-for="ticket in selectedObject.tickets"
              :key="ticket.id"
              v-model="form.tickets[ticket.internal_name]"
              v-non-negative
              type="number"
              outlined
              dense
              class="mb-2"
              :label="ticket['name' + localeSuffix] || ticket.name"
              :hint="ticket['helper_text' + localeSuffix] || ticket.helper_text"
              placeholder="0"
              persistent-hint
            ></v-text-field>

            <v-checkbox
              v-for="service in serviceOptions"
              :key="service.internal_name"
              v-model="form.services[service.internal_name]"
              :disabled="isServiceDisabled(service)"
              hide-details
              dense
              class="mt-2"
              :label="serviceTitle(service.internal_name)"
            ></v-checkbox>
          </template>
        </v-card-text>
        <!-- «Добавить группу» (шеф, 25.10.2026): как в множественной заявке —
             посчитанный музей уходит в список справа, форма ждёт следующий
             музей; количество билетов подставляется прежнее, его можно
             поменять. -->
        <v-card-actions class="flex-wrap justify-end calc-actions">
          <!-- Общий итог по добавленным группам показываем здесь же, под
               кнопкой (шеф, 26.10.2026: «нет общей цены» — карточка со списком
               групп ниже «Стоимости» была не на виду, особенно на телефоне). -->
          <div
            v-if="groups.length"
            class="me-auto"
          >
            <div class="font-weight-semibold text--primary">
              {{ $t('Итого по всем группам') }} ({{ groups.length }}): {{ money(groupsTotal) }}
            </div>
            <div
              v-if="addedHint"
              class="text-xs success--text"
            >
              {{ addedHint }}
            </div>
          </div>
          <span
            v-else-if="addedHint"
            class="text-sm success--text me-auto"
          >{{ addedHint }}</span>
          <v-btn
            color="primary"
            outlined
            :disabled="!canAdd"
            @click="addGroup"
          >
            {{ $t('Добавить группу') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      md="6"
    >
      <v-card class="calc-result">
        <v-progress-linear
          v-if="loadingQuote"
          indeterminate
          absolute
          top
        ></v-progress-linear>
        <v-card-title>{{ $t('Стоимость') }}</v-card-title>
        <v-card-text>
          <template v-if="!selectedObject">
            <p class="text--secondary mb-0">
              {{ isEquipment ? $t('Выберите музей — расчёт появится здесь') : $t('Выберите музей и объект — расчёт появится здесь') }}
            </p>
            <p
              v-if="groups.length"
              class="mt-3 mb-0 font-weight-semibold text--primary"
            >
              {{ $t('Итого по всем группам') }} ({{ groups.length }}): {{ money(groupsTotal) }}
            </p>
          </template>
          <p
            v-else-if="personsCount === 0"
            class="text--secondary mb-0"
          >
            {{ isEquipment ? $t('Укажите количество аппаратов') : $t('Укажите количество билетов') }}
          </p>
          <p
            v-else-if="!quote"
            class="text--secondary mb-0"
          >
            {{ $t('Считаем…') }}
          </p>
          <p
            v-else-if="quote.reason === 'no_session'"
            class="mb-0"
          >
            {{ $t('Расчёт недоступен: войдите в кабинет заново') }}
          </p>
          <p
            v-else-if="!quote.tickets_applicable"
            class="mb-0"
          >
            {{ isEquipment ? $t('Для этой услуги цены в прайс-листе нет. Сумму рассчитает менеджер после подачи заказа.') : $t('Для этого объекта цены в прайс-листе нет. Сумму рассчитает менеджер после подачи заявки.') }}
          </p>
          <template v-else>
            <div class="text--secondary text-sm mb-3">
              {{ selectedObjectName }} · {{ $t('Прайс-лист') }} «{{ quote.price_list }}»
            </div>
            <div
              v-for="(line, index) in resultLines"
              :key="index"
              class="calc-line"
            >
              <div class="calc-line__label">
                <div>{{ line.label }}</div>
                <div class="text--secondary text-xs">
                  {{ line.detail }}
                </div>
              </div>
              <div class="text-no-wrap">
                {{ money(line.amount) }}
              </div>
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="calc-line calc-line--total">
              <div class="font-weight-semibold text--primary">
                {{ $t('Итого') }}
              </div>
              <div class="font-weight-semibold text--primary text-xl text-no-wrap">
                {{ money(quote.amount) }}
              </div>
            </div>
            <!-- Текущая группа ещё не добавлена: показываем, сколько будет
                 вместе с уже добавленными. -->
            <div
              v-if="groups.length"
              class="calc-line"
            >
              <div class="text--secondary">
                {{ $t('Вместе с добавленными группами ({n})', { n: groups.length }) }}
              </div>
              <div class="font-weight-semibold text--primary text-no-wrap">
                {{ money(groupsTotal + Number(quote.amount || 0)) }}
              </div>
            </div>
            <p
              v-if="quote.note"
              class="text--secondary text-sm mt-3 mb-0"
            >
              {{ keepSums(quote.note) }}
            </p>
          </template>
        </v-card-text>
        <v-card-actions class="justify-end flex-wrap">
          <v-btn
            text
            :to="{ name: 'pricelist' }"
          >
            {{ $t('Прайс-лист') }}
          </v-btn>
          <!-- Шеф, 27.10.2026: расчёт устроил — заявки подаются прямо отсюда,
               без набора заново в форме заявки. -->
          <v-btn
            v-if="hasEquipmentGroups"
            color="primary"
            :to="{ name: 'new-request-rg-ag' }"
          >
            {{ $t('Заказать радиогид / аудиогид') }}
          </v-btn>
          <v-btn
            v-else
            color="primary"
            :disabled="!groups.length && !canAdd"
            @click="openApply"
          >
            {{ $t('Подать заявку') }}
          </v-btn>
        </v-card-actions>
        <v-card-text
          v-if="hasEquipmentGroups"
          class="pt-0 caption text--secondary"
        >
          {{ $t('В расчёте есть заказ радиогида или аудиогида: такие заказы подаются в разделе «Радиогид / Аудиогид», заявки в музеи — из расчёта без него.') }}
        </v-card-text>
      </v-card>

      <!-- Добавленные группы: музей за музеем, как в множественной заявке,
           и общий итог. -->
      <v-card
        v-if="groups.length"
        class="mt-6"
      >
        <v-card-title>{{ $t('Расчёт по группам') }}</v-card-title>
        <v-card-subtitle>{{ $t('Музеев в расчёте') }}: {{ groups.length }}</v-card-subtitle>
        <v-card-text>
          <div
            v-for="(group, idx) in groups"
            :key="group.key"
            class="calc-line calc-group"
          >
            <div class="calc-line__label">
              <div class="font-weight-semibold text--primary">
                {{ idx + 1 }}. {{ group.museumName }} — {{ group.objectName }}
              </div>
              <div class="text--secondary text-xs">
                {{ formatDay(group.date) }} · {{ group.persons }} {{ $t('чел.') }} · {{ group.summary }}
              </div>
            </div>
            <div class="d-flex align-center text-no-wrap">
              <span class="me-1">{{ money(group.amount) }}</span>
              <v-btn
                icon
                small
                color="error"
                :title="$t('Убрать')"
                @click="removeGroup(idx)"
              >
                <v-icon size="18">
                  {{ icons.mdiClose }}
                </v-icon>
              </v-btn>
            </div>
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="calc-line calc-line--total">
            <div class="font-weight-semibold text--primary">
              {{ $t('Итого по всем группам') }}
            </div>
            <div class="font-weight-semibold text--primary text-xl text-no-wrap">
              {{ money(groupsTotal) }}
            </div>
          </div>
        </v-card-text>
        <!-- Выгрузка в Word (27.10.2026): для себя — с логотипом и
             реквизитами АТГ, для клиента турфирмы — без них. -->
        <v-card-text class="pt-0">
          <v-text-field
            v-model="docTitle"
            :label="$t('Заголовок документа')"
            outlined
            dense
            hide-details="auto"
            maxlength="200"
            class="mb-3"
          ></v-text-field>
          <div
            class="d-flex flex-wrap"
            style="gap: 8px"
          >
            <!-- Одна кнопка — расчёт с логотипом (название от начальника,
                 27.10.2026); кнопка «без логотипа» убрана по его же просьбе. -->
            <v-btn
              color="primary"
              :loading="downloading === 'atg'"
              :disabled="!!downloading"
              @click="downloadDoc('atg')"
            >
              <v-icon
                left
                size="20"
              >
                {{ icons.mdiFileWordOutline }}
              </v-icon>
              {{ $t('Скачать расчёт') }}
            </v-btn>
          </div>
          <p
            v-if="downloadError"
            class="error--text text-sm mt-3 mb-0"
          >
            {{ downloadError }}
          </p>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            text
            @click="clearGroups"
          >
            {{ $t('Очистить расчёт') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>

    <!-- Подача заявок по расчёту: на каждый музей — отдельная заявка, как в
         множественной заявке. Время посещения выбирается здесь, страна и
         гид — общие на все музеи. -->
    <v-dialog
      v-model="isApplyOpen"
      max-width="720"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title>{{ $t('Подача заявок по расчёту') }}</v-card-title>
        <v-card-text>
          <p class="text-sm text--secondary">
            {{ $t('На каждый музей уйдёт отдельная заявка. Выберите время посещения и укажите данные гида.') }}
          </p>
          <div
            v-for="(group, idx) in groups"
            :key="group.key"
            class="mb-4"
          >
            <div class="font-weight-semibold text--primary">
              {{ idx + 1 }}. {{ group.museumName }} — {{ group.objectName }}
            </div>
            <div class="text--secondary text-xs mb-2">
              {{ formatDay(group.date) }} · {{ group.persons }} {{ $t('чел.') }} · {{ money(group.amount) }}
            </div>
            <v-select
              v-model="group.schedule"
              :items="group.slots"
              item-text="name"
              item-value="id"
              :label="$t('Выберите время')"
              :loading="group.slotsLoading"
              :disabled="!group.slots.length"
              :error-messages="dateProblem(group) ? [dateProblem(group)] : (group.slotsError ? [group.slotsError] : [])"
              outlined
              dense
              hide-details="auto"
            ></v-select>
          </div>
          <v-select
            v-if="needsCountry"
            v-model="applyCountry"
            :items="countries"
            item-text="name"
            item-value="system_id"
            :label="$t('Выберите страну')"
            :no-data-text="$t('Ничего не найдено')"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          ></v-select>
          <v-text-field
            v-model="applyGuideName"
            :label="$t('Имя гида')"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="applyGuidePhone"
            :label="$t('Телефон гида')"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="applyRefNumber"
            :label="$t('Ref номер (необязательно)')"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          ></v-text-field>
          <v-textarea
            v-model="applyComment"
            :label="$t('Пожелания')"
            rows="2"
            auto-grow
            outlined
            dense
            hide-details="auto"
          ></v-textarea>
          <p
            v-if="applyError"
            class="error--text text-sm mt-3 mb-0"
            style="white-space: pre-line"
          >
            {{ applyError }}
          </p>
        </v-card-text>
        <v-card-actions class="justify-end flex-wrap">
          <v-btn
            text
            :disabled="applying"
            @click="isApplyOpen = false"
          >
            {{ $t('Закрыть') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="applying"
            @click="submitApply"
          >
            {{ $t('Отправить') }} ({{ groups.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isApplyResultOpen"
      width="500"
    >
      <v-card>
        <v-card-title>{{ applyResultOk ? $t('Успешно') : $t('Заявка не отправлена') }}</v-card-title>
        <v-card-text style="white-space: pre-line">
          {{ applyResultText }}
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            :color="applyResultOk ? 'primary' : 'error'"
            @click="closeApplyResult"
          >
            {{ $t('Ок') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Datepicker from '@/components/Datepicker.vue'
import axios from '@axios'
import store from '@/store'
import { useRouter } from '@core/utils'
import {
  groupRefNo,
  groupTypeChoices,
  guideServicesCount,
  makeRefNo,
  refNoBase,
  SCHOOL_NOTE_MARK,
  toLocalDay,
  sortMuseumsByName,
} from '@/utils/helper'
import { mdiClose, mdiFileWordOutline } from '@mdi/js'
import { computed, ref, watch } from '@vue/composition-api'

// Суффиксы переведённых полей справочников (name_ru_RU и т.п.) — как в форме заявки.
const LOCALE_SUFFIX = {
  ru: '_ru_RU',
  en: '_en_US',
  zh: '_zh_cn',
  fa: '_fa_IR',
  vi: '_vi',
}

// Категория группы по умолчанию — по языку кабинета; остальные языки — иностранные туристы.
const DEFAULT_TYPE = { ru: 'ru', zh: 'cn' }

// Пробел перед знаком рубля неразрывный: «1 000 ₽» не рвётся на две строки.
const money = value => `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value)}\u00a0₽`
const keepSums = text => String(text || '').replace(/(\d) (?=\d{3}\b)/g, '$1 ').replace(/(\d) ₽/g, '$1 ₽')

// Дата группы в списке: «25.10.2026».
const formatDay = iso => {
  if (!iso) return ''
  const [year, month, day] = String(iso).split('-')

  return `${day}.${month}.${year}`
}

// Перехватчик axios не отклоняет обещание при ошибке, а возвращает её как
// ответ, поэтому данные проверяем, а не доверяем им.
const listOf = response => (response && Array.isArray(response.data) ? response.data : [])

export default {
  components: { Datepicker },
  setup(props, { root }) {
    const locale = localStorage.getItem('locale') || 'ru'
    const localeSuffix = LOCALE_SUFFIX[locale] || '_ru_RU'

    // Все поля перечислены сразу: Vue 2 следит только за ними.
    const form = ref({
      // Что считаем: заявка в музей или отдельный заказ оборудования
      // (радиогид в город, радиогид в музей, аудиогид).
      kind: 'museum',
      type: DEFAULT_TYPE[locale] || 'en',
      school: false,
      museum: null,
      object: null,
      date: toLocalDay(),
      tickets: {},
      services: {},

      // заказ оборудования
      devices: 0,
      delivery: false,
      deliveryZone: '',
    })
    const museums = ref([])
    const objects = ref([])
    const equipmentObjects = ref([])
    const selectedObject = ref(null)
    const isEquipment = computed(() => form.value.kind !== 'museum')
    const kindOptions = computed(() => [
      { name: root.$t('Заявка в музей'), value: 'museum' },
      { name: root.$t('Радиогид в город'), value: 'rg-city' },
      { name: root.$t('Радиогид в музей'), value: 'rg-museum' },
      { name: root.$t('Аудиогид'), value: 'ag' },
    ])
    const kindName = kind => (kindOptions.value.find(item => item.value === kind) || {}).name || ''

    // Количество билетов последней добавленной группы — для следующего музея,
    // и подсказка «группа добавлена» под формой.
    let lastTickets = {}
    const addedHint = ref('')
    const loadingMuseums = ref(false)
    const loadingObjects = ref(false)
    const museumSearch = ref('')
    const datepickerKey = ref(0)

    // «Китайские туристы» — только клиентам с открытым китайским прайсом
    // (шеф, 30.10.2026); в китайском кабинете вместо неё — иностранные.
    const typeOptions = computed(() => (locale === 'zh'
      ? groupTypeChoices(
        [{ name: root.$t('Китайские туристы'), value: 'cn' }],
        { name: root.$t('Иностранные туристы'), value: 'en' },
      )
      : groupTypeChoices([
        { name: root.$t('Российские туристы'), value: 'ru' },
        { name: root.$t('Иностранные туристы'), value: 'en' },
        { name: root.$t('Китайские туристы'), value: 'cn' },
      ])))
    if (!typeOptions.value.some(item => item.value === form.value.type)) {
      form.value.type = typeOptions.value[0].value
    }

    const filteredMuseums = computed(() => {
      const query = (museumSearch.value || '').toLowerCase().trim()
      if (!query) return museums.value
      const key = `name${localeSuffix}`

      // Выбранный музей оставляем, чтобы поле не теряло подпись.
      return museums.value.filter(
        museum => String(museum[key] || museum.name || '').toLowerCase().includes(query)
          || museum.id === form.value.museum,
      )
    })

    const fetchMuseums = () => {
      loadingMuseums.value = true
      axios.get(`/api/v1/museums/?type=${form.value.type}`)
        .then(response => {
          // Музеи по алфавиту на языке кабинета (шеф, 02.11.2026).
          museums.value = sortMuseumsByName(listOf(response))
          loadingMuseums.value = false
        })
        .catch(() => {
          museums.value = []
          loadingMuseums.value = false
        })
    }

    const fetchObjects = () => {
      loadingObjects.value = true
      axios.get(`/api/v1/museums-objects/?museum_id=${form.value.museum}&type=${form.value.type}`)
        .then(response => {
          objects.value = listOf(response)
          loadingObjects.value = false
        })
        .catch(() => {
          objects.value = []
          loadingObjects.value = false
        })
    }

    // Объекты радиогида и аудиогида — справочник формы заказа, отбор как там:
    // в город — объект получения в городе, в музей — остальные радиогиды.
    const fetchEquipmentObjects = () => {
      loadingObjects.value = true
      axios.get('/api/v1/museums-for-rgag/')
        .then(response => {
          const { kind } = form.value
          equipmentObjects.value = listOf(response).filter(item => {
            if (kind === 'ag') return item.is_audioguide_request === true
            if (kind === 'rg-city') return item.is_radioguide_request === true && item.is_radioguide_request_in_city === true

            return item.is_radioguide_request === true && item.is_radioguide_request_in_city === false
          })
          loadingObjects.value = false

          // Радиогид в город — объект один, выбираем его сами.
          if (kind === 'rg-city' && equipmentObjects.value.length) {
            const [city] = equipmentObjects.value
            form.value.object = city.system_id
            selectedObject.value = city
          }
        })
        .catch(() => {
          equipmentObjects.value = []
          loadingObjects.value = false
        })
    }

    // Расчёт: тот же адрес и то же тело, что у формы заявки.
    const quote = ref(null)
    const loadingQuote = ref(false)
    let quoteTimer = null
    let quoteSerial = 0

    const devicesCount = computed(() => Math.max(0, Math.trunc(Number(form.value.devices) || 0)))
    const personsCount = computed(() => (isEquipment.value
      ? devicesCount.value
      : Object.values(form.value.tickets || {})
        .reduce((acc, value) => acc + Math.max(0, Number(value) || 0), 0)))

    // Порог обязательного радиогида у выбранного объекта (0 — порога нет).
    const rgThreshold = computed(() => (isEquipment.value || !selectedObject.value
      ? 0
      : Math.max(0, Number(selectedObject.value.amount_for_rg) || 0)))
    const rgMandatory = computed(() => rgThreshold.value > 0 && personsCount.value >= rgThreshold.value)

    const isForeign = computed(() => form.value.type === 'en')

    const serviceOptions = computed(() => {
      const list = (selectedObject.value && selectedObject.value.services) || []
      if (!isForeign.value || list.some(item => item.internal_name === 'ag')) return list

      return [...list, { internal_name: 'ag', is_fallback: true }]
    })

    // Радиогид и аудиогид — одно из двух (у объекта с такой логикой и у иностранцев).
    const isServiceDisabled = service => {
      const obj = selectedObject.value || {}
      if (!obj.service_logic && !isForeign.value) return false
      const services = form.value.services || {}
      if (service.internal_name === 'rg') return services.ag === true
      if (service.internal_name === 'ag') return services.rg === true

      return false
    }

    const serviceTitle = name => {
      if (name === 'guide') return root.$t('Экскурсионное обслуживание')
      if (name === 'delivery') return root.$t('доставка')
      if (isEquipment.value) return name === 'ag' ? root.$t('Аудиогид') : root.$t('Радиогид')
      const found = serviceOptions.value.find(item => item.internal_name === name)
      if (!found) return name
      if (found.is_fallback) return root.$t('Аудиогид')

      return found[`name${localeSuffix}`] || found.name || name
    }

    // Категории билетов в админке подписаны «Взрослые – количество» /
    // «Adults - amount»: в расшифровке хвост про количество лишний.
    const shortTicketName = name => String(name).replace(/\s*[-–—]\s*(количество|amount|quantity)\s*$/i, '')

    const ticketTitle = (name, bucket) => {
      const list = (selectedObject.value && selectedObject.value.tickets) || []
      const found = list.find(item => item.internal_name === name)
      if (found) return shortTicketName(found[`name${localeSuffix}`] || found.name || name)

      return bucket === 'common' ? root.$t('Основные билеты') : root.$t('Льготные билеты')
    }

    const fetchQuote = () => {
      if (!selectedObject.value || personsCount.value === 0) {
        quote.value = null
        loadingQuote.value = false

        return
      }
      quoteSerial += 1
      const serial = quoteSerial
      loadingQuote.value = true

      // Заказ оборудования — своя ручка: аппараты и зона доставки.
      const request = isEquipment.value
        ? {
          url: '/api/v1/payments/equipment-price/',
          data: {
            system_id: form.value.object,
            pass_date: form.value.date,
            devices: devicesCount.value,
            delivery_zone: form.value.kind !== 'ag' && form.value.delivery ? form.value.deliveryZone : '',
          },
        }
        : {
          url: '/api/v1/payments/price/',
          data: {
            system_id: form.value.object,
            pass_date: form.value.date,
            tickets: form.value.tickets,
            services: form.value.services,
            group_type: form.value.type,
            school: form.value.school,

            // Как в форме заявки: одно обслуживание на каждые N человек по норме объекта.
            qty_guide: guideServicesCount(personsCount.value, selectedObject.value && selectedObject.value.persons_per_guide),
          },
        }
      axios({ method: 'post', ...request })
        .then(response => {
          if (serial !== quoteSerial) return
          const result = (response && response.data) || {}
          quote.value = typeof result.applicable === 'undefined'
            ? { tickets_applicable: false, reason: 'error' }
            : result
          loadingQuote.value = false
        })
        .catch(() => {
          if (serial !== quoteSerial) return
          quote.value = { tickets_applicable: false, reason: 'error' }
          loadingQuote.value = false
        })
    }

    watch(
      () => [
        form.value.object,
        form.value.date,
        form.value.type,
        form.value.school,
        form.value.kind,
        form.value.devices,
        form.value.delivery,
        form.value.deliveryZone,
        JSON.stringify(form.value.tickets),
        JSON.stringify(form.value.services),
      ],
      () => {
        window.clearTimeout(quoteTimer)
        quoteTimer = window.setTimeout(fetchQuote, 300)
      },
    )

    const resetObject = () => {
      form.value.object = null
      form.value.tickets = {}
      form.value.services = {}
      form.value.delivery = false
      form.value.deliveryZone = ''
      selectedObject.value = null
      quote.value = null
    }
    const onKindChange = () => {
      form.value.museum = null
      museums.value = []
      objects.value = []
      equipmentObjects.value = []
      resetObject()
      if (isEquipment.value) {
        fetchEquipmentObjects()
      } else {
        fetchMuseums()
      }
    }
    const onEquipmentObjectChange = id => {
      selectedObject.value = equipmentObjects.value.find(item => item.system_id === id) || null
      quote.value = null
      addedHint.value = ''
      datepickerKey.value += 1
    }

    const onTypeChange = () => {
      form.value.museum = null
      museums.value = []
      objects.value = []
      resetObject()
      fetchMuseums()
    }

    const onMuseumChange = () => {
      museumSearch.value = ''
      objects.value = []
      resetObject()
      if (form.value.museum) fetchObjects()
    }

    const onObjectChange = id => {
      const obj = objects.value.find(item => item.system_id === id) || null
      selectedObject.value = obj
      quote.value = null
      if (!obj) return
      addedHint.value = ''
      const tickets = {}
      const ticketKinds = obj.tickets || []
      ticketKinds.forEach(item => {
        // Количество из только что добавленной группы — «то же кол-во»
        // для следующего музея; у новой категории билетов — ноль.
        tickets[item.internal_name] = Object.prototype.hasOwnProperty.call(lastTickets, item.internal_name)
          ? lastTickets[item.internal_name]
          : 0
      })
      form.value.tickets = tickets
      const services = {}
      const serviceKinds = obj.services || []
      serviceKinds.forEach(item => {
        services[item.internal_name] = false
      })
      if (isForeign.value && !('ag' in services)) services.ag = false
      form.value.services = services
      datepickerKey.value += 1
    }

    const selectedObjectName = computed(() => {
      const obj = selectedObject.value || {}

      return obj[`name${localeSuffix}`] || obj.name || ''
    })

    // Расшифровка расчёта: подпись, «сколько × почём», сумма.
    const resultLines = computed(() => {
      const lines = (quote.value && quote.value.lines) || []

      return lines.map(line => {
        if (line.code === 'group') {
          const limit = line.tier_max ? ` ${root.$t('до {n} чел.', { n: line.tier_max })}` : ''

          return {
            code: 'group',
            label: `${root.$t('Цена за группу')}${limit}`,
            detail: root.$t('{n} чел.', { n: line.qty }),
            qty: 1,
            unit: line.amount,
            amount: line.amount,
          }
        }
        if (line.code === 'guide') {
          return {
            code: 'guide',
            label: root.$t('Экскурсионное обслуживание'),
            detail: `${line.qty} × ${money(line.unit)}`,
            qty: line.qty,
            unit: line.unit,
            amount: line.amount,
          }
        }
        if (line.code === 'delivery') {
          return {
            code: 'delivery',
            label: root.$t('Доставка'),
            detail: line.zone === 'out' ? root.$t('за пределами КАД') : root.$t('в пределах КАД'),
            qty: 1,
            unit: line.amount,
            amount: line.amount,
          }
        }
        if (line.code === 'service') {
          const hasDelivery = line.order !== null && line.order !== undefined
          const delivery = hasDelivery ? ` + ${money(line.order)} ${root.$t('за доставку')}` : ''

          return {
            code: 'service',
            label: isEquipment.value ? root.$t('Аппараты') : serviceTitle(line.service),
            detail: `${line.qty} × ${money(line.unit)}${delivery}`,
            qty: line.qty,
            unit: line.unit,
            order: hasDelivery ? line.order : null,
            amount: line.amount,
          }
        }

        return {
          code: 'ticket',
          label: ticketTitle(line.ticket, line.bucket),
          detail: `${line.qty} × ${money(line.unit)}`,
          qty: line.qty,
          unit: line.unit,
          amount: line.amount,
        }
      })
    })

    const missingTitles = computed(() => ((quote.value && quote.value.missing_services) || []).map(serviceTitle))

    // «Добавить группу» (шеф, 25.10.2026): посчитанный музей уходит в список
    // со своей расшифровкой и суммой, форма освобождается под следующий музей,
    // количество билетов запоминаем и подставляем ему. Итог — по всем группам.
    const groups = ref([])
    const canAdd = computed(() => !!(
      quote.value && quote.value.tickets_applicable && personsCount.value > 0 && !loadingQuote.value
    ))
    const museumName = () => {
      if (isEquipment.value) return kindName(form.value.kind)
      const museum = museums.value.find(item => item.id === form.value.museum) || {}

      return museum[`name${localeSuffix}`] || museum.name || ''
    }

    // Заказ оборудования подаётся в разделе «Радиогид / Аудиогид», не отсюда.
    const hasEquipmentGroups = computed(() => groups.value.some(group => group.kind !== 'museum'))

    // Выгрузка расчёта файлом Word (27.10.2026, правка начальника): «для
    // себя» — с логотипом и реквизитами АТГ, «для клиента» турфирмы — без них.
    // Заголовок подставляем сами, его можно поправить перед скачиванием.
    const docTitle = ref('')
    const downloading = ref('')
    const downloadError = ref('')
    const defaultDocTitle = () => {
      const days = groups.value.map(group => group.date).sort()
      const range = days.length && days[0] !== days[days.length - 1]
        ? `${formatDay(days[0])} – ${formatDay(days[days.length - 1])}`
        : formatDay(days[0])

      // Категория группы и «N чел.» — только для заявок в музеи: у заказа
      // оборудования категории нет, а число — это аппараты.
      const museumGroups = groups.value.filter(group => group.kind === 'museum')
      const persons = museumGroups.map(group => group.persons)
      const same = persons.length && persons.every(value => value === persons[0])
      const who = museumGroups.length
        ? (typeOptions.value.find(item => item.value === form.value.type) || {}).name || ''
        : ''

      return [root.$t('Расчёт стоимости'), range, who, same ? root.$t('{n} чел.', { n: persons[0] }) : ''].filter(Boolean).join(' ')
    }
    const downloadDoc = mode => {
      if (!groups.value.length || downloading.value) return
      downloading.value = mode
      downloadError.value = ''
      axios({
        method: 'post',
        url: '/api/v1/pricelist/calculation/',
        responseType: 'blob',
        data: {
          mode,
          title: docTitle.value || defaultDocTitle(),
          groups: groups.value.map(group => ({
            date: group.date,
            museum: group.museumName,
            object: group.objectName,
            persons: group.persons,
            amount: group.amount,
            missing: group.missing,
            rows: group.rows,
          })),
        },
      })
        .then(response => {
          downloading.value = ''
          const blob = response && response.data
          if (!(blob instanceof Blob) || !response.status || response.status >= 400) {
            downloadError.value = root.$t('Не удалось подготовить файл. Попробуйте ещё раз.')

            return
          }
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${docTitle.value || root.$t('Расчёт стоимости')}.docx`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.setTimeout(() => window.URL.revokeObjectURL(url), 10000)
        })
        .catch(() => {
          downloading.value = ''
          downloadError.value = root.$t('Не удалось подготовить файл. Попробуйте ещё раз.')
        })
    }

    const addGroup = () => {
      if (!canAdd.value) return
      const objectName = selectedObjectName.value
      const museum = museumName()

      // Строки для файла Word: «наименование — кол-во — цена — сумма»,
      // доставка услуги — отдельной строкой, как в образце начальника.
      const rows = []
      resultLines.value.forEach(line => {
        let name = `${objectName}, ${line.label}`
        if (line.code === 'guide') name = `${line.label} — ${objectName}`
        if (line.code === 'service') name = isEquipment.value ? `${objectName}, ${line.label}` : `${line.label} — ${museum}`
        if (line.code === 'delivery') name = `${objectName}, ${line.label} (${line.detail})`
        rows.push({
          name,
          qty: line.qty,
          unit: line.unit,
          amount: line.code === 'service' && line.order ? line.qty * line.unit : line.amount,
        })
        if (line.code === 'service' && line.order) {
          rows.push({
            name: `${line.label}, ${root.$t('доставка')}`,
            qty: 1,
            unit: line.order,
            amount: line.order,
          })
        }
      })
      groups.value.push({
        key: `${Date.now()}-${groups.value.length}`,
        museumName: museum,
        objectName,
        date: form.value.date,
        persons: personsCount.value,
        summary: resultLines.value.map(line => `${line.label} ${line.detail}`).join(' · '),
        amount: Number(quote.value.amount || 0),
        missing: [...missingTitles.value],
        rows,
        kind: form.value.kind,

        // Для подачи заявки прямо из калькулятора (все ключи сразу — Vue 2).
        museumId: form.value.museum,
        objectId: form.value.object,
        personsPerGuide: selectedObject.value.persons_per_guide || '0',
        tickets: { ...form.value.tickets },
        services: { ...form.value.services },
        guides: String(guideServicesCount(personsCount.value, selectedObject.value.persons_per_guide)),
        type: form.value.type,
        school: form.value.school,
        minDaysBefore: Number(selectedObject.value.min_days_before || 0),
        schedule: null,
        slots: [],
        slotsLoading: false,
        slotsError: '',
      })
      if (!docTitle.value) docTitle.value = defaultDocTitle()
      if (isEquipment.value) {
        addedHint.value = root.$t('Заказ добавлен в расчёт.')
        const city = form.value.kind === 'rg-city'
        resetObject()
        form.value.devices = 0

        // У радиогида в город объект один — оставляем его выбранным.
        if (city && equipmentObjects.value.length) {
          const [first] = equipmentObjects.value
          form.value.object = first.system_id
          selectedObject.value = first
        }

        return
      }
      lastTickets = { ...form.value.tickets }
      addedHint.value = root.$t('Группа добавлена в расчёт. Выберите следующий музей — количество билетов подставится прежнее.')
      form.value.museum = null
      objects.value = []
      resetObject()
    }

    const removeGroup = idx => {
      groups.value.splice(idx, 1)
    }
    const clearGroups = () => {
      groups.value = []
      addedHint.value = ''
      docTitle.value = ''
      downloadError.value = ''
    }

    // Подача заявок по расчёту (шеф, 27.10.2026): та же отправка, что у
    // множественной заявки — на каждый музей своя заявка, последовательно,
    // со стопом на первой ошибке; у нескольких музеев общий Ref# с номером
    // и пометка «ГРУППОВАЯ ЗАЯВКА», как там.
    const { router } = useRouter()
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    const RUSSIA_COUNTRY_ID = 267
    const CHINA_COUNTRY_ID = 100
    const isApplyOpen = ref(false)
    const applying = ref(false)
    const applyError = ref('')
    const applyCountry = ref(null)
    const applyGuideName = ref(userData.use_default_data ? userData.default_guide_name || '' : '')
    const applyGuidePhone = ref(userData.use_default_data ? userData.default_guide_phone || '' : '')
    const applyRefNumber = ref('')
    const applyComment = ref('')
    const isApplyResultOpen = ref(false)
    const applyResultOk = ref(false)
    const applyResultText = ref('')
    const countries = computed(() => store.getters['countries/getCountries'] || [])
    const needsCountry = computed(() => groups.value.some(group => group.type === 'en'))
    const countryFor = type => {
      if (type === 'cn') return CHINA_COUNTRY_ID
      if (type === 'ru') return RUSSIA_COUNTRY_ID

      return applyCountry.value
    }

    const loadSlots = async group => {
      Object.assign(group, { slotsLoading: true, slotsError: '' })
      const res = await store.dispatch('createOrder/checkSchedule', { date: group.date, museumObjectId: group.objectId })
      const slots = res.ok ? (res.schedule || []).filter(item => !String(item.name || '').includes('График')) : []
      let error = ''
      if (!res.ok) error = res.error
      else if (!slots.length) error = root.$t('На эту дату записи нет — измените дату в калькуляторе')
      let { schedule } = group
      if (schedule && !slots.some(item => item.id === schedule)) schedule = null
      if (!schedule && slots.length === 1) schedule = slots[0].id
      Object.assign(group, {
        slots, slotsError: error, schedule, slotsLoading: false,
      })
    }

    const openApply = () => {
      // Посчитанный, но не добавленный музей — добавляем сами.
      if (!groups.value.length && canAdd.value) addGroup()
      if (!groups.value.length || hasEquipmentGroups.value) return
      applyError.value = ''
      isApplyOpen.value = true
      if (needsCountry.value && !countries.value.length) store.dispatch('countries/fetchCountries')
      if (needsCountry.value && !applyCountry.value && userData.use_default_data && userData.default_guide_country) {
        const found = countries.value.find(item => item.name === userData.default_guide_country)
        if (found) applyCountry.value = found.system_id
      }
      groups.value.forEach(group => {
        if (!group.slots.length) loadSlots(group)
      })
    }

    const describeGroup = group => `${group.museumName} — ${group.objectName}, ${formatDay(group.date)}`

    // Срок подачи как в форме заявки: не раньше, чем сегодня плюс срок у
    // объекта. Калькулятор считает на любую дату, а заявку на слишком близкую
    // офис не примет — говорим об этом до отправки.
    const tooEarly = group => {
      const from = new Date()
      from.setDate(from.getDate() + Number(group.minDaysBefore || 0))

      return group.date < toLocalDay(from)
    }
    const dateProblem = group => (tooEarly(group)
      ? root.$t('Заявка на эту дату не принимается: не раньше чем за {n} дн. — измените дату в калькуляторе', { n: group.minDaysBefore })
      : '')

    const submitApply = async () => {
      applyError.value = ''
      const problems = []
      if (needsCountry.value && !applyCountry.value) problems.push(root.$t('Выберите страну'))
      groups.value.forEach(group => {
        if (tooEarly(group)) problems.push(`${describeGroup(group)}: ${dateProblem(group)}`)
        else if (!group.schedule) problems.push(`${describeGroup(group)}: ${root.$t('выберите время')}`)
      })
      if (problems.length) {
        applyError.value = problems.join('\n')

        return
      }
      applying.value = true

      // Перед отправкой перепроверяем время у каждого музея: пока заполняли,
      // место могли занять. Отправляем только когда всё на месте.
      for (let i = 0; i < groups.value.length; i += 1) {
        const group = groups.value[i]
        // eslint-disable-next-line no-await-in-loop
        const res = await store.dispatch('createOrder/checkSchedule', { date: group.date, museumObjectId: group.objectId })
        if (!res.ok) problems.push(`${describeGroup(group)}: ${res.error}`)
        else if (!(res.schedule || []).some(item => item.id === group.schedule)) {
          problems.push(`${describeGroup(group)}: ${root.$t('это время больше недоступно, выберите другое')}`)
          Object.assign(group, { slots: res.schedule.filter(item => !String(item.name || '').includes('График')), schedule: null })
        }
      }
      if (problems.length) {
        applying.value = false
        applyError.value = problems.join('\n')

        return
      }

      const multi = groups.value.length > 1
      const base = applyRefNumber.value.trim() || makeRefNo(
        (store.getters['upcoming/getUpcomingList'] || [])
          .map(request => refNoBase((request.data && request.data.refno) || ''))
          .filter(Boolean),
      )
      const comment = applyComment.value.trim()
      const entries = groups.value.map((group, index) => ({
        museum: group.museumId,
        system_id: group.objectId,
        date: group.date,
        schedule: group.schedule,
        persons_per_guide: group.personsPerGuide,
        country: countryFor(group.type),
        guide: applyGuideName.value,
        guidePhone: applyGuidePhone.value,
        tickets: Object.fromEntries(Object.entries(group.tickets).map(([key, value]) => [key, String(Math.max(0, Math.trunc(Number(value) || 0)))])),
        services: group.services,
        guides: group.guides,
        notes: [group.school ? SCHOOL_NOTE_MARK : '', multi ? 'ГРУППОВАЯ ЗАЯВКА' : '', comment].filter(Boolean).join(' '),
        type: group.type,
        refNumber: multi ? groupRefNo(base, index) : applyRefNumber.value.trim(),
      }))

      const results = await store.dispatch('createOrder/createGroupOrder', entries)
      applying.value = false
      const okResults = results.filter(result => result.ok)
      const failed = results.find(result => !result.ok)
      isApplyOpen.value = false
      if (!failed) {
        applyResultOk.value = true
        applyResultText.value = `${root.$t('Отправлено заявок: {n}', { n: okResults.length })} ${okResults.map(result => `№${result.id}`).join(', ')}`
        try {
          sessionStorage.setItem('askFeedback', '1')
        } catch (e) {
          // приватный режим — просто не спросим отзыв
        }
        clearGroups()
      } else {
        applyResultOk.value = false
        const lines = [describeGroup(groups.value[failed.index]), '', `${root.$t('Причина')}: ${failed.error}`]
        if (okResults.length) {
          lines.push('', root.$t('До сбоя в офис успели уйти заявки {list} — они уже в «Заказах», подавать их заново не нужно.', { list: okResults.map(result => `№${result.id}`).join(', ') }))
        }
        lines.push('', root.$t('Остальные музеи оставлены в расчёте — исправьте и отправьте заново.'))
        applyResultText.value = lines.join('\n')
        const sent = okResults.map(result => result.index)
        groups.value = groups.value.filter((_, idx) => !sent.includes(idx))
      }
      isApplyResultOpen.value = true
    }

    const closeApplyResult = () => {
      isApplyResultOpen.value = false
      if (applyResultOk.value) router.push({ name: 'upcoming-requests' })
    }
    const groupsTotal = computed(() => groups.value.reduce((acc, group) => acc + group.amount, 0))
    const groupsMissing = computed(() => {
      const titles = []
      groups.value.forEach(group => {
        group.missing.forEach(title => {
          if (!titles.includes(title)) titles.push(title)
        })
      })

      return titles
    })

    fetchMuseums()

    return {
      form,
      localeSuffix,
      typeOptions,
      kindOptions,
      isEquipment,
      equipmentObjects,
      onKindChange,
      onEquipmentObjectChange,
      hasEquipmentGroups,
      filteredMuseums,
      museumSearch,
      objects,
      selectedObject,
      selectedObjectName,
      loadingMuseums,
      loadingObjects,
      datepickerKey,
      personsCount,
      serviceOptions,
      isServiceDisabled,
      serviceTitle,
      quote,
      loadingQuote,
      resultLines,
      missingTitles,
      money,
      keepSums,
      rgThreshold,
      rgMandatory,
      onTypeChange,
      onMuseumChange,
      onObjectChange,
      groups,
      addedHint,
      canAdd,
      addGroup,
      removeGroup,
      clearGroups,
      groupsTotal,
      groupsMissing,
      formatDay,
      docTitle,
      downloading,
      downloadError,
      downloadDoc,
      isApplyOpen,
      applying,
      applyError,
      applyCountry,
      applyGuideName,
      applyGuidePhone,
      applyRefNumber,
      applyComment,
      isApplyResultOpen,
      applyResultOk,
      applyResultText,
      countries,
      needsCountry,
      dateProblem,
      openApply,
      submitApply,
      closeApplyResult,
      icons: { mdiClose, mdiFileWordOutline },
    }
  },
}
</script>

<style lang="scss" scoped>
.calc-result {
  position: relative;
}

.calc-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 6px 0;
}

.calc-line + .calc-line {
  border-top: 1px dashed rgba(94, 86, 105, 0.14);
}

.calc-line--total {
  align-items: center;
}

.calc-group + .calc-group {
  border-top: 1px dashed rgba(94, 86, 105, 0.14);
}

.calc-actions {
  gap: 8px;
}
</style>
